import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { protocolPageData, lineUrl } from "@/mocks/siteData";
import SEO from "@/components/base/SEO";

function useRevealOnScroll() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-6");
          }
        });
      },
      { threshold: 0.1 }
    );
    el.querySelectorAll(".reveal").forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, []);
  return ref;
}

/* Scan line animation for tech cards */
function ScanLineOverlay() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none rounded-2xl">
      <div
        className="absolute left-0 right-0 h-px"
        style={{
          background: "linear-gradient(90deg, transparent, rgba(34,211,238,0.5), transparent)",
          animation: "protocolScan 2.5s linear infinite",
          boxShadow: "0 0 6px rgba(34,211,238,0.4)",
        }}
      />
      <style>{`
        @keyframes protocolScan {
          0% { top: 0%; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
      `}</style>
    </div>
  );
}

/* Tech decorative card for hero right side */
function HeroTechVisual() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const metrics = [
    { label: "COLLAGEN", value: `${62 + (tick % 8)}%`, color: "#FACC6B" },
    { label: "METABOLISM", value: `${55 + (tick % 6)}%`, color: "#22D3EE" },
    { label: "STRESS", value: `${68 - (tick % 5)}%`, color: "#FF8A4C" },
    { label: "HYDRATION", value: `${74 + (tick % 4)}%`, color: "#35E0B9" },
  ];

  return (
    <div className="relative w-full max-w-sm mx-auto lg:mx-0">
      <div className="relative aspect-[4/5] rounded-2xl border border-cyber-gold/20 bg-abyss-800/60 backdrop-blur-sm overflow-hidden p-6 md:p-8">
        <ScanLineOverlay />

        {/* Circuit corners */}
        <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-cyber-gold/25 rounded-tl-2xl" />
        <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-cyber-gold/25 rounded-tr-2xl" />
        <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-cyber-gold/25 rounded-bl-2xl" />
        <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-cyber-gold/25 rounded-br-2xl" />

        {/* Center core */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-28 h-28 md:w-36 md:h-36">
            <div className="absolute inset-0 rounded-full border border-cyber-gold/15 animate-pulse" />
            <div className="absolute inset-3 rounded-full border border-neon-cyan/15" />
            <div className="absolute inset-6 rounded-full border border-cyber-gold/10" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-cyber-gold/8 border border-cyber-gold/30 flex items-center justify-center">
                <i className="ri-cpu-line text-cyber-gold text-2xl md:text-3xl" />
              </div>
            </div>
          </div>
        </div>

        {/* Orbiting metric chips */}
        {metrics.map((m, i) => {
          const angles = [-25, 35, -155, 145];
          const angle = (angles[i] * Math.PI) / 180;
          const radius = 44;
          const x = 50 + radius * Math.cos(angle);
          const y = 50 + radius * Math.sin(angle);
          return (
            <div
              key={m.label}
              className="absolute px-2 py-1 rounded-md border font-mono text-base whitespace-nowrap"
              style={{
                left: `${x}%`,
                top: `${y}%`,
                transform: "translate(-50%, -50%)",
                borderColor: `${m.color}30`,
                color: m.color,
                background: `${m.color}08`,
              }}
            >
              <span className="opacity-60">{m.label}</span>{" "}
              <span className="font-bold">{m.value}</span>
            </div>
          );
        })}

        {/* Bottom status bar */}
        <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between font-mono text-base text-ghost-dim">
          <span className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-neon-cyan animate-pulse" />
            PROTOCOL: ASSESSING
          </span>
          <span className="text-cyber-gold/50">v3.0.1</span>
        </div>
      </div>

      {/* Floating badges */}
      <div className="absolute -bottom-3 -left-3 card-cyber px-4 py-2">
        <span className="font-mono text-neon-cyan text-base">3</span>
        <span className="text-ghost text-base ml-2">大評估模組</span>
      </div>
      <div className="absolute -top-3 -right-3 w-14 h-14 rounded-full border border-cyber-gold/20 flex items-center justify-center bg-abyss-800/80 backdrop-blur-sm">
        <i className="ri-shield-keyhole-line text-cyber-gold text-xl" />
      </div>
    </div>
  );
}

/* FAQ Accordion */
function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-cyber-gold/10">
      <button
        className="w-full flex items-start justify-between gap-4 py-5 text-left group"
        onClick={() => setOpen(!open)}
      >
        <span className="text-[1.0625rem] md:text-[1.1875rem] font-medium text-ghost group-hover:text-cyber-gold transition-colors">
          {q}
        </span>
        <div className="w-8 h-8 rounded-lg border border-cyber-gold/20 flex items-center justify-center shrink-0 mt-0.5 group-hover:border-cyber-gold/50 transition-colors">
          <i className={`ri-arrow-down-s-line text-cyber-gold text-lg transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </button>
      <div
        className="overflow-hidden transition-all duration-500"
        style={{ maxHeight: open ? 400 : 0, opacity: open ? 1 : 0 }}
      >
        <p className="pb-5 text-ghost-muted text-[1.0625rem] md:text-[1.1875rem] leading-[1.75] pl-0 md:pl-2">
          {a}
        </p>
      </div>
    </div>
  );
}

export default function Protocol() {
  const revealRef = useRevealOnScroll();
  const data = protocolPageData;

  return (
    <>
      <SEO
        title="逆齡路線方案｜時光逆行四部曲｜時光逆行駭客"
        description="時光逆行四部曲：掃描狀態、找出關鍵節點、設計逆齡路線、持續追蹤更新。三大逆齡評估模組為不同族群設計專屬方案。先評估，再設計，不是一張療程價目表。"
        keywords="逆齡路線圖,時光逆行四部曲,逆齡評估,曾驛翔,醫美方案,抗衰老"
        canonicalPath="/protocol"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "逆齡路線方案｜時光逆行駭客",
          "description": "時光逆行四部曲與三大逆齡評估模組，為不同族群設計專屬逆齡方案。",
        }}
      />
    <div className="min-h-screen bg-abyss-900 circuit-bg" ref={revealRef}>
      {/* ===== HERO ===== */}
      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero" />
        <div className="absolute inset-0 circuit-bg opacity-20" />
        <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-8" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="prot-circuit" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M0 50h40M60 50h40M50 0v40M50 60v40" stroke="#FACC6B" strokeWidth="0.5" fill="none" />
              <circle cx="50" cy="50" r="2" fill="#FACC6B" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#prot-circuit)" />
        </svg>

        <div className="relative z-10 mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            {/* Left: Text */}
            <div className="lg:col-span-7 text-center lg:text-left">
              <span className="section-label reveal opacity-0 translate-y-6 transition-all duration-700">
                {data.heroTagline}
              </span>
              <h1 className="mt-5 text-[2rem] leading-[1.3] md:text-[2.75rem] md:leading-[1.25] font-bold text-ghost reveal opacity-0 translate-y-6 transition-all duration-700 delay-100">
                {data.heroTitleLine1}
                <br />
                <span className="text-gradient-gold">{data.heroTitleLine2}</span>
              </h1>
              <p className="mt-5 md:mt-6 text-ghost-muted text-[1.1875rem] leading-[1.75] max-w-xl mx-auto lg:mx-0 reveal opacity-0 translate-y-6 transition-all duration-700 delay-200">
                {data.heroDesc}
              </p>
              <p className="mt-4 text-ghost-dim text-[1rem] leading-[1.7] max-w-xl mx-auto lg:mx-0 reveal opacity-0 translate-y-6 transition-all duration-700 delay-300">
                {data.heroSupplement}
              </p>
              <div className="mt-8 md:mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start reveal opacity-0 translate-y-6 transition-all duration-700 delay-400">
                <a
                  href={lineUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-line text-[1.0625rem]"
                >
                  <i className="ri-line-fill" />
                  加入 LINE 諮詢
                </a>
                <Link to="/booking" className="btn-gold text-[1.0625rem]">
                  預約逆齡評估
                </Link>
              </div>
            </div>

            {/* Right: Tech Visual */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end reveal opacity-0 translate-y-6 transition-all duration-700 delay-500">
              <HeroTechVisual />
            </div>
          </div>
        </div>
      </section>

      {/* ===== THREE ASSESSMENT MODULES ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.modules.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.modules.title}
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.modules.items.map((mod, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 120}ms` }}
              >
                {/* Header */}
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ background: `${mod.accent}12`, border: `1px solid ${mod.accent}25` }}
                  >
                    <i className={`${mod.icon} text-xl`} style={{ color: mod.accent }} />
                  </div>
                  <div>
                    <span className="font-mono text-base" style={{ color: mod.accent }}>
                      MODULE_{mod.num}
                    </span>
                    <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-ghost leading-tight">
                      {mod.title}
                    </h3>
                  </div>
                </div>

                {/* Desc bullets */}
                <ul className="space-y-3">
                  {mod.desc.map((d, di) => (
                    <li key={di} className="flex items-start gap-3">
                      <span
                        className="w-1.5 h-1.5 rounded-full mt-2.5 shrink-0"
                        style={{ background: mod.accent }}
                      />
                      <span className="text-ghost-muted text-[1.0625rem] leading-[1.7]">{d}</span>
                    </li>
                  ))}
                </ul>

                {/* Audience tag */}
                <div
                  className="mt-6 px-4 py-2.5 rounded-lg text-[1rem] font-medium"
                  style={{
                    background: `${mod.accent}10`,
                    color: mod.accent,
                    border: `1px solid ${mod.accent}20`,
                  }}
                >
                  {mod.audience}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FOUR-STEP PROTOCOL ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.fourSteps.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.fourSteps.title}
            </h2>
          </div>

          {/* Connected flow */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {data.fourSteps.steps.map((step, idx) => (
              <div
                key={idx}
                className="relative reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 120}ms` }}
              >
                {/* Connector line (desktop only, between cards) */}
                {idx < 3 && (
                  <div className="hidden lg:block absolute top-12 -right-3 w-6 z-10">
                    <div className="flex items-center">
                      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${step.accent}40, transparent)` }} />
                      <i className="ri-arrow-right-line text-base shrink-0" style={{ color: `${step.accent}60` }} />
                    </div>
                  </div>
                )}

                <div className="card-cyber p-6 md:p-7 h-full">
                  <ScanLineOverlay />

                  {/* Step number + code */}
                  <div className="flex items-center justify-between mb-5">
                    <span
                      className="font-mono text-[1.25rem] font-bold"
                      style={{ color: step.accent }}
                    >
                      {step.num}
                    </span>
                    <span
                      className="font-mono text-base px-2.5 py-1 rounded-md"
                      style={{
                        background: `${step.accent}12`,
                        color: step.accent,
                        border: `1px solid ${step.accent}25`,
                      }}
                    >
                      {step.code}
                    </span>
                  </div>

                  {/* Icon */}
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4"
                    style={{ background: `${step.accent}10`, border: `1px solid ${step.accent}20` }}
                  >
                    <i className={`${step.icon} text-2xl`} style={{ color: step.accent }} />
                  </div>

                  <h3 className="text-[1.125rem] md:text-[1.25rem] font-semibold text-ghost mb-3">
                    {step.title}
                  </h3>
                  <p className="text-ghost-muted text-[1.0625rem] leading-[1.7]">
                    {step.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PLAN OPTIONS ===== */}
      <section className="py-20 md:py-28 bg-abyss-800/50">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="text-center mb-12 md:mb-16 reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.plans.label}</span>
            <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.plans.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.plans.items.map((plan, idx) => (
              <div
                key={idx}
                className="card-cyber p-6 md:p-8 text-center reveal opacity-0 translate-y-6 transition-all duration-700"
                style={{ transitionDelay: `${idx * 120}ms` }}
              >
                {/* Tag circle */}
                <div
                  className="w-16 h-16 rounded-full border-2 mx-auto mb-5 flex items-center justify-center"
                  style={{ borderColor: `${plan.accent}40`, background: `${plan.accent}08` }}
                >
                  <span className="font-mono text-[1.5rem] font-bold" style={{ color: plan.accent }}>
                    {plan.tag}
                  </span>
                </div>

                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ background: `${plan.accent}10`, border: `1px solid ${plan.accent}20` }}
                >
                  <i className={`${plan.icon} text-2xl`} style={{ color: plan.accent }} />
                </div>

                <h3 className="text-[1.25rem] md:text-[1.375rem] font-semibold text-ghost mb-2">
                  {plan.title}
                </h3>
                <p className="text-ghost-muted text-[1.0625rem] leading-[1.7] mb-4">
                  {plan.desc}
                </p>
                <div
                  className="px-4 py-3 rounded-xl text-[1rem] font-medium"
                  style={{
                    background: `${plan.accent}10`,
                    color: plan.accent,
                    border: `1px solid ${plan.accent}20`,
                  }}
                >
                  {plan.highlight}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-10 md:mb-12 reveal opacity-0 translate-y-6 transition-all duration-700">
              <span className="section-label">{data.faq.label}</span>
              <h2 className="mt-4 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
                {data.faq.title}
              </h2>
            </div>
            <div className="card-cyber p-6 md:p-10 reveal opacity-0 translate-y-6 transition-all duration-700">
              {data.faq.items.map((item, idx) => (
                <FAQItem key={idx} q={item.q} a={item.a} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== BOTTOM CTA ===== */}
      <section className="py-20 md:py-28">
        <div className="mx-auto px-4 md:px-6 lg:px-8 w-full" style={{ maxWidth: 1280 }}>
          <div className="card-cyber p-10 md:p-16 text-center reveal opacity-0 translate-y-6 transition-all duration-700">
            <span className="section-label">{data.bottomCta.label}</span>
            <h2 className="mt-5 text-[1.5rem] leading-[1.4] md:text-[2.125rem] md:leading-[1.35] font-bold text-ghost">
              {data.bottomCta.title}
            </h2>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href={lineUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-line text-[1.0625rem]"
              >
                <i className="ri-line-fill" />
                {data.bottomCta.ctaLine}
              </a>
              <Link to="/booking" className="btn-gold text-[1.0625rem]">
                <i className="ri-calendar-check-line" />
                {data.bottomCta.ctaBooking}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
}